/**
 * RegionContext — single-source-of-truth for regional preference + catalogue.
 *
 * Resolution chain (first match wins, on mount):
 *   1. authenticated account.region (and persists to localStorage)
 *   2. localStorage["concretecontrol.region"]
 *   3. GET /api/region/detect   → uses `detected_region`, persists to localStorage
 *   4. fallback "US_CA_MX"
 *
 * Catalogue (GET /api/region/catalogue) is fetched in parallel on mount and
 * cached for the lifetime of the provider. All region-aware UI reads from the
 * cache; no per-component round-trips.
 *
 * setRegion(newRegion):
 *   - anonymous: writes localStorage + state.
 *   - authenticated: PUT /api/account/region first, then commits state on 2xx.
 *     Reverts + toasts on error.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { authService, formatApiError } from "@/services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REGION_STORAGE_KEY = "concretecontrol.region";

export const VALID_REGIONS = ["HK", "US_CA_MX", "EU", "UK", "AU"];
const DEFAULT_REGION = "US_CA_MX";

/* Phase 2F · canonical DISPLAY roster (11 regions, ordered).
 * Each entry maps a display code → backend region code (or null if pipeline).
 * The 5 "ready" entries persist server-side; pipeline entries render with a
 * muted chip and are not selectable via setRegion (toast "Coming soon"). */
export const DISPLAY_REGIONS = [
    { code: "US",        label: "United States",  short_label: "US",      backend: "US_CA_MX", pipeline: false },
    { code: "CAN",       label: "Canada",         short_label: "CAN",     backend: "US_CA_MX", pipeline: false },
    { code: "UK",        label: "United Kingdom", short_label: "UK",      backend: "UK",       pipeline: false },
    { code: "AUS",       label: "Australia",      short_label: "AUS",     backend: "AU",       pipeline: false },
    { code: "EU",        label: "Continental Europe", short_label: "EU",  backend: "EU",       pipeline: false },
    { code: "KSA",       label: "Saudi Arabia",   short_label: "KSA",     backend: null,       pipeline: true  },
    { code: "UAE",       label: "United Arab Emirates", short_label: "UAE", backend: null,     pipeline: true  },
    { code: "OMAN",      label: "Oman",           short_label: "Oman",    backend: null,       pipeline: true  },
    { code: "QATAR",     label: "Qatar",          short_label: "Qatar",   backend: null,       pipeline: true  },
    { code: "HK",        label: "Hong Kong",      short_label: "HK",      backend: "HK",       pipeline: false },
    { code: "SG",        label: "Singapore",      short_label: "SG",      backend: null,       pipeline: true  },
];

// Fallback catalogue used only while the network fetch is in flight. Mirrors
// the priority fields the picker reads (label/short_label/currency).
const FALLBACK_CATALOGUE = {
    HK: { label: "Hong Kong", short_label: "HK", currency: "USD", currency_symbol: "$", standards: [], footer_line: "", pricing: {} },
    US_CA_MX: { label: "United States", short_label: "US", currency: "USD", currency_symbol: "$", standards: [], footer_line: "", pricing: {} },
    EU: { label: "Continental Europe", short_label: "EU", currency: "USD", currency_symbol: "$", standards: [], footer_line: "", pricing: {} },
    UK: { label: "United Kingdom", short_label: "UK", currency: "USD", currency_symbol: "$", standards: [], footer_line: "", pricing: {} },
    AU: { label: "Australia", short_label: "AUS", currency: "USD", currency_symbol: "$", standards: [], footer_line: "", pricing: {} },
};

const isValidRegion = (r) => typeof r === "string" && VALID_REGIONS.includes(r);

const readLocalStorage = () => {
    try {
        const v = localStorage.getItem(REGION_STORAGE_KEY);
        return isValidRegion(v) ? v : null;
    } catch {
        return null;
    }
};

const writeLocalStorage = (region) => {
    try {
        if (isValidRegion(region)) localStorage.setItem(REGION_STORAGE_KEY, region);
    } catch {
        /* storage not available — ignore */
    }
};

const RegionContext = createContext(null);

export const RegionProvider = ({ children }) => {
    const { account, status } = useAuth();

    const [region, setRegionState] = useState(DEFAULT_REGION);
    const [catalogue, setCatalogue] = useState(FALLBACK_CATALOGUE);
    const [loading, setLoading] = useState(true);
    const [detectionSource, setDetectionSource] = useState("fallback");

    // Track whether the user has explicitly set a region this session, so the
    // auth-status effect doesn't keep overwriting their choice on re-renders.
    const userPickedRef = useRef(false);

    // Catalogue fetch — fire-and-forget once, replace fallback on success.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/region/catalogue`);
                if (!cancelled && data && typeof data === "object") {
                    setCatalogue(data);
                }
            } catch {
                /* keep fallback */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Initial resolution: wait until auth status settles ("authenticated" or
    // "anonymous"), then run the resolution chain exactly once.
    useEffect(() => {
        if (status === "loading") return;
        if (userPickedRef.current) {
            // user already picked during this session; do not override.
            setLoading(false);
            return;
        }
        let cancelled = false;

        const accountRegion = account?.region;
        if (status === "authenticated" && isValidRegion(accountRegion)) {
            setRegionState(accountRegion);
            writeLocalStorage(accountRegion);
            setDetectionSource("account");
            setLoading(false);
            return;
        }

        const stored = readLocalStorage();
        if (isValidRegion(stored)) {
            setRegionState(stored);
            setDetectionSource("localStorage");
            setLoading(false);
            return;
        }

        (async () => {
            try {
                const { data } = await axios.get(`${API}/region/detect`);
                if (cancelled) return;
                if (isValidRegion(data?.detected_region)) {
                    setRegionState(data.detected_region);
                    writeLocalStorage(data.detected_region);
                    setDetectionSource(data.confidence || "ip");
                } else {
                    setDetectionSource("fallback");
                }
            } catch {
                if (!cancelled) setDetectionSource("fallback");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [status, account?.id, account?.region]);

    const setRegion = useCallback(async (newRegion) => {
        if (!isValidRegion(newRegion)) {
            throw new Error(`Invalid region: ${newRegion}`);
        }
        const previous = region;
        if (newRegion === previous) return;

        // Optimistic update — feels instant. Revert below on failure.
        userPickedRef.current = true;
        setRegionState(newRegion);
        writeLocalStorage(newRegion);

        if (status === "authenticated") {
            try {
                await authService.http.put("/account/region", { region: newRegion });
                setDetectionSource("account");
            } catch (err) {
                // Revert state + storage on failure.
                setRegionState(previous);
                writeLocalStorage(previous);
                toast.error(formatApiError(err) || "Could not update region.");
                throw err;
            }
        } else {
            setDetectionSource("localStorage");
        }
    }, [region, status]);

    const regionData = catalogue[region] || FALLBACK_CATALOGUE[region] || FALLBACK_CATALOGUE[DEFAULT_REGION];

    const value = useMemo(
        () => ({
            region,
            setRegion,
            catalogue,
            regionData,
            currency: regionData.currency,
            currencySymbol: regionData.currency_symbol,
            standards: regionData.standards || [],
            footerLine: regionData.footer_line || "",
            pricing: regionData.pricing || {},
            loading,
            detectionSource,
        }),
        [region, setRegion, catalogue, regionData, loading, detectionSource],
    );

    return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = () => {
    const ctx = useContext(RegionContext);
    if (!ctx) throw new Error("useRegion must be used within a RegionProvider");
    return ctx;
};
